import React, { useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Timer, Plus, Copy, Bookmark, ChevronDown, CheckCircle2, Minus, Trash2, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import { doc, getDoc } from 'firebase/firestore';

import db, { auth } from '../../firebase';
import { getWorkout, getTemplates, addExercise, addExercises, createTemplate, deleteWorkout, deleteExercise, Exercise } from '../../features/workoutSlice';
import exercises from '../../data/exercises.json';
import LoadingSpinner from './LoadingSpinner';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Toggle } from '../ui/toggle';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Drawer, DrawerTrigger, DrawerContent, DrawerClose } from '../ui/drawer';

interface SetInfo {
  id: string;
  reps: number;
  weight: number;
  status: string;
  timeSeconds?: number;
}

interface ExerciseSetsMap {
  [exerciseId: string]: SetInfo[];
}

const WorkoutDetail: React.FC = () => {
  const { workoutID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = auth.currentUser;
  const [expandedExercises, setExpandedExercises] = useState<Set<string>>(new Set());
  const [actionMenuExercise, setActionMenuExercise] = useState<Exercise | null>(null);

  // Exercise selection state
  const [selectedExercise, setSelectedExercise] = useState({} as Exercise);
  const [numberOfSets, setNumberOfSets] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const templateNameRef = useRef<HTMLInputElement>(null);

  const allBodyParts = useMemo(() => {
    const parts = new Set(exercises.flatMap(exercise => exercise.bodyParts));
    return Array.from(parts);
  }, []);

  const { data: workoutData } = useQuery({
    queryKey: ["workouts", user!.uid, "workout", workoutID],
    queryFn: () => getWorkout(workoutID!),
    enabled: !!workoutID,
    staleTime: 0,
  });

  // Fetch and cache all set recordings for this workout
  const { data: exerciseSets = {} } = useQuery({
    queryKey: ['allSetRecordings', workoutID],
    queryFn: async () => {
      if (!workoutData?.exercises || !user || !workoutID) return {};

      // Fetch all set recordings in parallel for speed
      const results = await Promise.all(
        workoutData.exercises.map(async (exercise: Exercise) => {
          const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exercise.pfxExerciseId);
          const docSnap = await getDoc(setRecordingsRef);
          return {
            id: exercise.pfxExerciseId,
            sets: docSnap.exists() ? docSnap.data().sets || [] : []
          };
        })
      );

      const setsMap: ExerciseSetsMap = {};
      results.forEach(({ id, sets }) => {
        setsMap[id] = sets;
      });

      return setsMap;
    },
    enabled: !!workoutData?.exercises && !!user && !!workoutID,
    staleTime: 0, // Always check for fresh data
  });

  const templateQuery = useQuery({
    queryKey: ["workouts", user!.uid, "templates"],
    queryFn: () => getTemplates(),
  });

  const addExerciseMutation = useMutation({
    mutationKey: [workoutID, 'addExercise', selectedExercise.pfxExerciseId],
    mutationFn: () => addExercise(workoutID!, selectedExercise, numberOfSets, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout'] });
      queryClient.invalidateQueries({ queryKey: ['allSetRecordings', workoutID] });
      setSelectedExercise({} as Exercise);
      setNumberOfSets(0);
    },
  });

  const addExercisesMutation = useMutation({
    mutationKey: [workoutID, 'addExercises'],
    mutationFn: (templateExercises: Exercise[]) => addExercises(workoutID!, templateExercises, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout'] });
      queryClient.invalidateQueries({ queryKey: ['allSetRecordings', workoutID] });
    },
  });

  const handleAddExercise = () => {
    addExerciseMutation.mutate();
  };

  const handleAddExercisesFromTemplate = () => {
    const selectedTemplate = templateQuery.data?.find((template: any) => template.name === selectedTemplateId);
    if (selectedTemplate && selectedTemplate.exercises) {
      addExercisesMutation.mutate(selectedTemplate.exercises);
    }
  };

  const handleCreateTemplate = () => {
    const templateName = templateNameRef.current?.value;
    if (templateName && workoutID) {
      createTemplate(workoutID, templateName, workoutData?.exercises);
      queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'templates'] });
    }
  };

  const handleExerciseChange = (exerciseId: string) => {
    const exercise = exercises.find(ex => ex.pfxExerciseId === exerciseId);
    if (exercise) {
      setSelectedExercise(exercise as Exercise);
    }
  };

  const handleDeleteWorkout = async () => {
    if (workoutID) {
      await deleteWorkout(workoutID);
      queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout'] });
      navigate('/workouts');
    }
  };

  const handleAddSetToExercise = async (exercise: Exercise) => {
    if (!workoutID || !user) return;

    const currentSets = exerciseSets[exercise.pfxExerciseId] || [];
    const newSet = {
      id: crypto.randomUUID(),
      reps: 0,
      weight: 0,
      status: 'incomplete'
    };
    const updatedSets = [...currentSets, newSet];

    // Optimistically update the cache immediately
    queryClient.setQueryData(['allSetRecordings', workoutID], (old: ExerciseSetsMap | undefined) => ({
      ...(old || {}),
      [exercise.pfxExerciseId]: updatedSets
    }));

    // Save to server
    const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exercise.pfxExerciseId);
    const { setDoc } = await import('firebase/firestore');
    await setDoc(setRecordingsRef, { sets: updatedSets }, { merge: true });

    // Invalidate queries to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ['setRecordings', workoutID, exercise.pfxExerciseId] });
    queryClient.invalidateQueries({ queryKey: ['allSetRecordings', workoutID] });

    setActionMenuExercise(null);
  };

  const handleRemoveSetFromExercise = async (exercise: Exercise) => {
    if (!workoutID || !user) return;

    const currentSets = exerciseSets[exercise.pfxExerciseId] || [];
    if (currentSets.length === 0) return;

    const updatedSets = currentSets.slice(0, -1);

    // Optimistically update the cache immediately
    queryClient.setQueryData(['allSetRecordings', workoutID], (old: ExerciseSetsMap | undefined) => ({
      ...(old || {}),
      [exercise.pfxExerciseId]: updatedSets
    }));

    // Save to server
    const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exercise.pfxExerciseId);
    const { setDoc } = await import('firebase/firestore');
    await setDoc(setRecordingsRef, { sets: updatedSets }, { merge: true });

    // Invalidate queries to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ['setRecordings', workoutID, exercise.pfxExerciseId] });
    queryClient.invalidateQueries({ queryKey: ['allSetRecordings', workoutID] });

    setActionMenuExercise(null);
  };

  const handleDeleteExercise = async (exercise: Exercise) => {
    if (!workoutID) return;

    await deleteExercise(workoutID, exercise.pfxExerciseId);
    queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout', workoutID] });
    setActionMenuExercise(null);
  };

  // Only show loading spinner on initial load when there's no cached data
  if (!workoutData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const workout = workoutData;
  const workoutDate = workout?.createdAt ? new Date(workout.createdAt) : new Date();
  const hour = workoutDate.getHours();
  const timeOfDay = hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening';

  const totalSets = Object.values(exerciseSets).reduce((acc, sets) => acc + sets.length, 0);

  // Calculate total duration from all completed sets
  const totalDurationSeconds = Object.values(exerciseSets).reduce((total, sets) => {
    return total + sets.reduce((setTotal, set) => setTotal + (set.timeSeconds || 0), 0);
  }, 0);

  // Calculate completed sets for progress bar
  const completedSetsCount = Object.values(exerciseSets).reduce((total, sets) => {
    return total + sets.filter(set => set.status === 'completed').length;
  }, 0);
  const totalSetsCount = Object.values(exerciseSets).reduce((total, sets) => total + sets.length, 0);
  const progressPercent = totalSetsCount > 0 ? (completedSetsCount / totalSetsCount) * 100 : 0;

  const formatDuration = (totalSeconds: number) => {
    if (totalSeconds === 0) return '--:--';
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins < 60) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}:${remainingMins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleExercise = (exerciseId: string) => {
    setExpandedExercises(prev => {
      const next = new Set(prev);
      if (next.has(exerciseId)) {
        next.delete(exerciseId);
      } else {
        next.add(exerciseId);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background">
        {/* Header */}
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/workouts')}
                className="flex items-center justify-center w-8 h-8 rounded-2xl bg-secondary"
              >
                <ArrowLeft className="h-[18px] w-[18px] text-foreground" />
              </button>
              <div className="flex flex-col gap-0.5">
                <h1 className="text-xl font-bold text-foreground">{timeOfDay} Workout</h1>
                <p className="text-[13px] text-muted-foreground">
                  {format(workoutDate, 'MMM d, yyyy')} · {format(workoutDate, 'h:mm a')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary">
                <Timer className="h-4 w-4" style={{ color: 'hsl(var(--tmw-accent))' }} />
                <span className="text-sm font-semibold text-foreground">{formatDuration(totalDurationSeconds)}</span>
              </div>
              <button
                onClick={handleDeleteWorkout}
                className="flex items-center justify-center w-8 h-8 rounded-2xl bg-destructive/10"
              >
                <Trash2 className="h-[18px] w-[18px] text-destructive" />
              </button>
            </div>
          </div>
        </div>

        {/* Exercises Counter */}
        <div className="px-5 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[13px] font-semibold text-muted-foreground">EXERCISES</p>
            <span className="text-[11px] text-muted-foreground">{completedSetsCount}/{totalSetsCount} sets</span>
          </div>
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${progressPercent}%`,
                backgroundColor: progressPercent === 100 ? 'hsl(142 71% 45%)' : 'hsl(var(--tmw-accent))'
              }}
            />
          </div>
        </div>
      </div>

      {/* Exercises List */}
      <div className="flex flex-col flex-1 min-h-0 px-5">
        <ScrollArea className="flex-1">
          <div className="flex flex-col pb-44">
            {workout?.exercises?.map((exercise: Exercise, index: number) => {
              const sets = exerciseSets[exercise.pfxExerciseId] || [];
              const completedSets = sets.filter((s: SetInfo) => s.status === 'completed').length;
              const totalExSets = sets.length;
              const isComplete = completedSets === totalExSets && totalExSets > 0;
              const inProgress = completedSets > 0 && completedSets < totalExSets;
              const isExpanded = expandedExercises.has(exercise.pfxExerciseId);

              return (
                <div
                  key={exercise.pfxExerciseId || index}
                  className="py-3 border-b border-border last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/workouts/${workoutID}/exercise/${exercise.pfxExerciseId}`}
                      className="flex items-center gap-2.5 flex-1 min-w-0"
                    >
                      <span className="text-[15px] font-semibold text-foreground truncate">{exercise.name}</span>
                    </Link>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-2 py-0.5 rounded text-[11px] font-semibold"
                        style={{
                          backgroundColor: isComplete ? 'rgba(22, 163, 74, 0.12)' : inProgress ? 'rgba(217, 119, 6, 0.12)' : 'hsl(var(--secondary))',
                          color: isComplete ? 'hsl(142 76% 36%)' : inProgress ? 'hsl(38 92% 50%)' : 'hsl(var(--muted-foreground))'
                        }}
                      >
                        {completedSets}/{totalExSets} sets
                      </span>
                      <button
                        onClick={() => toggleExercise(exercise.pfxExerciseId)}
                        className="p-1 -m-1"
                      >
                        {isComplete ? (
                          <CheckCircle2 className="h-[18px] w-[18px]" style={{ color: 'hsl(142 76% 36%)' }} />
                        ) : (
                          <ChevronDown
                            className="h-[18px] w-[18px] text-muted-foreground transition-transform duration-200"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          />
                        )}
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setActionMenuExercise(actionMenuExercise?.pfxExerciseId === exercise.pfxExerciseId ? null : exercise)}
                          className="p-1 -m-1"
                        >
                          <MoreVertical className="h-[18px] w-[18px] text-muted-foreground" />
                        </button>
                        {actionMenuExercise?.pfxExerciseId === exercise.pfxExerciseId && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setActionMenuExercise(null)} />
                            <div className="absolute right-0 top-8 z-50 flex gap-1.5 p-1.5 rounded-xl bg-white dark:bg-[hsl(240_5%_16%)] shadow-[0_4px_20px_rgba(0,0,0,0.15)]">
                              <button
                                onClick={() => handleRemoveSetFromExercise(exercise)}
                                className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-secondary transition-colors"
                                title="Remove Set"
                              >
                                <Minus className="h-5 w-5 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => handleAddSetToExercise(exercise)}
                                className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-secondary transition-colors"
                                title="Add Set"
                              >
                                <Plus className="h-5 w-5" style={{ color: 'hsl(var(--tmw-accent))' }} />
                              </button>
                              <button
                                onClick={() => handleDeleteExercise(exercise)}
                                className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-secondary transition-colors"
                                title="Delete Exercise"
                              >
                                <Trash2 className="h-5 w-5 text-destructive" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Set Chips - Collapsible */}
                  {totalExSets > 0 && isExpanded && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {sets.map((set: SetInfo, setIndex: number) => {
                        const isSetComplete = set.status === 'completed';
                        const formatSetTime = (seconds?: number) => {
                          if (!seconds) return '--';
                          const mins = Math.floor(seconds / 60);
                          const secs = seconds % 60;
                          return `${mins}:${secs.toString().padStart(2, '0')}`;
                        };

                        return (
                          <Link
                            key={set.id || setIndex}
                            to={`/workouts/${workoutID}/exercise/${exercise.pfxExerciseId}?set=${setIndex}`}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px]"
                            style={{
                              backgroundColor: isSetComplete ? 'rgba(34, 197, 94, 0.08)' : 'hsl(var(--secondary))',
                            }}
                          >
                            {isSetComplete ? (
                              <CheckCircle2 className="h-3 w-3" style={{ color: 'hsl(142 71% 45%)' }} />
                            ) : (
                              <div className="w-3 h-3 rounded-full border-[1.5px] border-muted-foreground/30" />
                            )}
                            <span
                              className="font-medium"
                              style={{ color: isSetComplete ? 'hsl(142 71% 45%)' : 'hsl(var(--muted-foreground))' }}
                            >
                              {setIndex + 1}
                            </span>
                            <span className="text-muted-foreground">·</span>
                            <span className={isSetComplete ? 'text-foreground' : 'text-muted-foreground'}>
                              {set.reps || 0}×{set.weight || 0}
                            </span>
                            <span className="text-muted-foreground">
                              {formatSetTime(set.timeSeconds)}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Actions */}
      <div className="fixed bottom-0 left-0 right-0 px-5 pb-5 pt-3 bg-background">
        <div className="flex gap-2 mb-2">
          {/* Add Exercise Drawer */}
          <Drawer>
            <DrawerTrigger asChild>
              <button
                className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-[10px]"
                style={{ backgroundColor: 'hsl(var(--tmw-accent))' }}
              >
                <Plus className="h-4 w-4 text-white" />
                <span className="text-[13px] font-semibold text-white">Add Exercise</span>
              </button>
            </DrawerTrigger>
            <DrawerContent className="p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Add Exercise</h3>

              <div className="flex flex-wrap gap-2 mb-4">
                {allBodyParts.map((part) => (
                  <Toggle
                    key={part}
                    pressed={activeFilters.includes(part)}
                    onPressedChange={() => {
                      setActiveFilters(current =>
                        current.includes(part)
                          ? current.filter(filter => filter !== part)
                          : [...current, part]
                      );
                    }}
                    className="capitalize text-xs py-1 px-3"
                    size='sm'
                  >
                    {part}
                  </Toggle>
                ))}
              </div>

              <Select onValueChange={handleExerciseChange}>
                <SelectTrigger aria-label="Exercise" className="w-full">
                  <SelectValue placeholder="Select an exercise" />
                </SelectTrigger>
                <SelectContent>
                  {exercises
                    .filter(exercise =>
                      activeFilters.length === 0 || exercise.bodyParts.some(part => activeFilters.includes(part))
                    )
                    .map((exercise, index) => (
                      <SelectItem key={index} value={exercise.pfxExerciseId}>{exercise.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <div className="flex items-center justify-between p-6 my-4 bg-secondary rounded-xl">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 rounded-full"
                  onClick={() => setNumberOfSets(Math.max(0, numberOfSets - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex flex-col items-center">
                  <span className="text-3xl font-semibold text-foreground">{numberOfSets}</span>
                  <span className="text-sm font-medium text-muted-foreground">SETS</span>
                </div>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 rounded-full"
                  onClick={() => setNumberOfSets(numberOfSets + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <DrawerClose asChild>
                <Button onClick={handleAddExercise} className="w-full bg-tmw-accent text-white hover:bg-tmw-accent/90">Add Exercise</Button>
              </DrawerClose>
            </DrawerContent>
          </Drawer>

          {/* From Template Drawer */}
          <Drawer>
            <DrawerTrigger asChild>
              <button className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-[10px] bg-secondary border border-border">
                <Copy className="h-4 w-4 text-foreground" />
                <span className="text-[13px] font-semibold text-foreground">Template</span>
              </button>
            </DrawerTrigger>
            <DrawerContent className="p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Add from Template</h3>
              <Select onValueChange={(templateId) => setSelectedTemplateId(templateId)}>
                <SelectTrigger aria-label="Template" className="w-full">
                  <SelectValue placeholder="Select a Template" />
                </SelectTrigger>
                <SelectContent>
                  {templateQuery.data?.map((template: any, index: number) => (
                    <SelectItem key={index} value={template.name}>{template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DrawerClose asChild>
                <Button onClick={handleAddExercisesFromTemplate} className="w-full mt-4 bg-tmw-accent text-white hover:bg-tmw-accent/90">Add Exercises</Button>
              </DrawerClose>
            </DrawerContent>
          </Drawer>
        </div>

        {/* Save Template Drawer */}
        <Drawer>
          <DrawerTrigger asChild>
            <button className="w-full flex items-center justify-center gap-1.5 h-10 rounded-[10px] border border-border">
              <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Save as Template</span>
            </button>
          </DrawerTrigger>
          <DrawerContent className="p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Create Template</h3>
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Template Name</label>
              <Input ref={templateNameRef} className="h-12" placeholder="My Template" />
            </div>
            <DrawerClose asChild>
              <Button onClick={handleCreateTemplate} className="w-full mt-4 bg-tmw-accent text-white hover:bg-tmw-accent/90">Create Template</Button>
            </DrawerClose>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
};

export default WorkoutDetail;
